import { useTranslation } from "react-i18next";
import {
  ArrowRight,
  ArrowUpRight,
  Check,
  Loader2,
  Sparkles,
} from "lucide-react";

import { OnboardingShell } from "@/components/onboarding/OnboardingShell";
import type { TenantCurrentResponse } from "@/services/authService";

export interface ReadyStepProps {
  onGoToDashboard: () => void;
  finishing: boolean;
  tenant: TenantCurrentResponse | null;
}

interface NextItemProps {
  num: string;
  title: string;
  body: string;
  /** All "next" cards behave like the primary "Ir al panel" CTA: they persist that onboarding
   * was completed and redirect to the dashboard. The user picks up these same items on the
   * dashboard onboarding checklist (cert / brand / cfe / accountant) where they can be
   * actioned or marked done individually. */
  onSelect: () => void;
  disabled?: boolean;
}

function NextItem({ num, title, body, onSelect, disabled }: NextItemProps): JSX.Element {
  return (
    <button
      type="button"
      className="fx-onb-ready-next-item"
      onClick={onSelect}
      disabled={disabled}
    >
      <span className="num">{num}</span>
      <span className="copy">
        <strong>{title}</strong>
        <span>{body}</span>
      </span>
      <ArrowUpRight className="arrow" aria-hidden />
    </button>
  );
}

function planLabel(planId: string | null | undefined): string {
  if (!planId) return "Tala";
  if (planId === "pro") return "Ombú";
  if (planId === "ultra") return "Ceibo";
  return "Tala";
}

function billingLabel(
  billingInterval: string | null | undefined,
  t: ReturnType<typeof useTranslation>["t"],
): string {
  return billingInterval === "annual"
    ? t("settings.plans.annual", "Anual")
    : t("settings.plans.monthly", "Mensual");
}

export function ReadyStep({
  onGoToDashboard,
  finishing,
  tenant,
}: ReadyStepProps): JSX.Element {
  const { t } = useTranslation();

  const companyName =
    tenant?.legalName?.trim() ||
    tenant?.name?.trim() ||
    t("onboarding.readyStep.companyFallback", "Tu empresa");
  const rut = tenant?.rut?.trim() ?? null;
  const plan = planLabel(tenant?.planId);
  const billing = billingLabel(tenant?.billingInterval, t);
  const dgiActive = Boolean(tenant?.hasDgiCredentials);
  /*
   * Whether WE are getting the certificate, or they are.
   *
   * Every "falta el certificado" string below was written for the manual path,
   * where the tenant fetches a .pfx and uploads it. On the Fiscalo-managed
   * path they have already paid at Abitab and ID Digital is issuing it to us —
   * we install it for them. Telling that tenant their remaining step is to
   * upload a file asks them to do something they cannot do and we never
   * wanted them to.
   */
  const fiscaloManagedCert = tenant?.onboardingCertChoice === "fiscalo";
  const certPendingOnUs = !dgiActive && fiscaloManagedCert;
  const dgiEnv = tenant?.dgiEnvironment ?? null;
  // The portal-user step is the only hard gate to leave /onboarding.
  // Reaching this screen without it means handleContinueFromDgi was
  // bypassed somehow — block the CTA defensively so the user isn't
  // stranded on a non-functional button.
  const portalUserReady = Boolean(tenant?.hasDgiPortalUser);

  return (
    <OnboardingShell
      form={
        <>
          <div className="fx-onb-ready-check" aria-hidden>
            <Check />
          </div>
          <span className="fx-onb-step-tag">
            <Sparkles aria-hidden style={{ width: 14, height: 14 }} />
            {dgiActive
              ? t("onboarding.readyStep.eyebrow", "Onboarding completo")
              : t(
                  certPendingOnUs
                    ? "onboarding.readyStep.eyebrowCertOnUs"
                    : "onboarding.readyStep.eyebrowCertPending",
                  certPendingOnUs
                    ? "Estamos gestionando tu certificado"
                    : "Falta el certificado digital",
                )}
          </span>
          <h1 className="fx-onb-h1" style={{ marginTop: 12, marginBottom: 14 }}>
            <span data-mag-private>{companyName}</span>{" "}
            <span className="ital">
              {dgiActive
                ? t(
                    "onboarding.readyStep.h1Accent",
                    "está lista para facturar.",
                  )
                : t(
                    "onboarding.readyStep.h1AccentCertPending",
                    "está casi lista.",
                  )}
            </span>
          </h1>
          <p className="fx-onb-lead">
            {dgiActive
              ? t(
                  "onboarding.readyStep.lead",
                  "Configuramos todo en DGI por vos. Ya podés emitir comprobantes — la sincronización con DGI corre en background y no tenés que volver a tocar nada.",
                )
              : t(
                  certPendingOnUs
                    ? "onboarding.readyStep.leadCertOnUs"
                    : "onboarding.readyStep.leadCertPending",
                  certPendingOnUs
                    ? "Configuramos todo en DGI por vos. Falta el certificado digital: ID Digital lo emite y nosotros lo instalamos en tu cuenta. Te avisamos por correo apenas puedas emitir."
                    : "Configuramos todo en DGI por vos. Para empezar a emitir facturas falta un único paso: subir tu certificado .pfx desde Ajustes — toma menos de un minuto.",
                )}
          </p>

          <div className="fx-onb-ready-summary">
            <div className="head">
              <span className="title">
                {t("onboarding.readyStep.summaryTitle", "Lo que dejamos guardado")}
              </span>
              <span className="date">
                {new Date().toLocaleDateString("es-UY", {
                  day: "2-digit",
                  month: "short",
                })}
              </span>
            </div>
            <div className="grid">
              <div className="cell">
                <span className="label">
                  {t("onboarding.readyStep.summaryCompany", "Empresa")}
                </span>
                <span className="value" data-mag-private>{companyName}</span>
                {rut ? (
                  <span className="sub" data-mag-private>RUT {rut}</span>
                ) : null}
              </div>
              <div className="cell">
                <span className="label">
                  {t("onboarding.readyStep.summaryPlan", "Plan")}
                </span>
                <span className="value">{plan}</span>
                <span className="sub">{billing}</span>
              </div>
              <div className="cell">
                <span className="label">
                  {t("onboarding.readyStep.summaryDgi", "DGI e-Factura")}
                </span>
                <span className="value">
                  {dgiActive
                    ? t("onboarding.readyStep.dgiActive", "Activo")
                    : t("onboarding.readyStep.dgiPending", "Pendiente")}
                </span>
                {dgiEnv ? (
                  <span className="sub">
                    {dgiEnv === "prod"
                      ? t("onboarding.readyStep.dgiProd", "Producción")
                      : t("onboarding.readyStep.dgiTest", "Homologación")}
                  </span>
                ) : null}
              </div>
            </div>
          </div>

          <div className="fx-onb-actions" style={{ marginTop: 28 }}>
            <div className="side" />
            <div className="side end">
              <button
                type="button"
                className="fx-onb-btn fx-onb-btn-accent"
                onClick={onGoToDashboard}
                disabled={finishing || !portalUserReady}
              >
                {finishing ? (
                  <Loader2 className="animate-spin" aria-hidden />
                ) : null}
                {t("onboarding.goToDashboard", "Ir al panel")}
                <ArrowRight aria-hidden />
              </button>
            </div>
          </div>
        </>
      }
      brand={
        <div className="fx-onb-brand-col fx-onb-ready-brand">
          <div>
            <span className="fx-onb-brand-eyebrow">
              <span className="dot" />
              {t("onboarding.readyStep.brandEyebrow", "Lo que sigue")}
            </span>
            <p className="fx-onb-brand-quote">
              {t("onboarding.readyStep.brandQuoteLead", "3 cosas que te")}{" "}
              <span className="accent">
                {t("onboarding.readyStep.brandQuoteAccent", "ahorran tiempo")}
              </span>{" "}
              {t("onboarding.readyStep.brandQuoteTail", "esta semana.")}
            </p>
          </div>
          <div className="fx-onb-ready-next-list">
            {dgiActive ? (
              <NextItem
                num="01"
                title={t("onboarding.readyStep.next1Title", "Importá tus clientes")}
                body={t(
                  "onboarding.readyStep.next1Body",
                  "Subí un CSV o cargalos manualmente. Detectamos RUTs y completamos datos por vos.",
                )}
                onSelect={onGoToDashboard}
                disabled={finishing || !portalUserReady}
              />
            ) : (
              <NextItem
                num="01"
                title={t(
                  certPendingOnUs
                    ? "onboarding.readyStep.next1TitleCertOnUs"
                    : "onboarding.readyStep.next1TitleCert",
                  certPendingOnUs
                    ? "Tu certificado está en camino"
                    : "Subí tu certificado digital",
                )}
                body={t(
                  certPendingOnUs
                    ? "onboarding.readyStep.next1BodyCertOnUs"
                    : "onboarding.readyStep.next1BodyCert",
                  certPendingOnUs
                    ? "ID Digital lo emite después del pago en Abitab y lo instalamos por vos. No tenés que hacer nada."
                    : "Es el .pfx que te entrega Abitab, Correo Uruguayo o Antel. Sin él no podemos firmar tus facturas.",
                )}
                onSelect={onGoToDashboard}
                disabled={finishing || !portalUserReady}
              />
            )}
            <NextItem
              num="02"
              title={t("onboarding.readyStep.next2Title", "Personalizá tus facturas")}
              body={t(
                "onboarding.readyStep.next2Body",
                "Subí tu logo y elegí el diseño del correo que reciben tus clientes.",
              )}
              onSelect={onGoToDashboard}
              disabled={finishing || !portalUserReady}
            />
            <NextItem
              num="03"
              title={t("onboarding.readyStep.next3Title", "Invitá a tu contador")}
              body={t(
                "onboarding.readyStep.next3Body",
                "Compartí acceso de solo-lectura para que vea cada movimiento sin tener que pedirte nada.",
              )}
              onSelect={onGoToDashboard}
              disabled={finishing || !portalUserReady}
            />
          </div>
          <a
            className="fx-onb-ready-feedback"
            href="mailto:soporte@fiscalo.uy"
          >
            <strong>
              {t(
                "onboarding.readyStep.feedbackLead",
                "¿Algo no funciona como esperabas?",
              )}
            </strong>
            <span>
              {t(
                "onboarding.readyStep.feedbackTail",
                "Escribinos — respondemos en menos de 1 hora.",
              )}
            </span>
          </a>
        </div>
      }
    />
  );
}
